import React, { FormEvent, useCallback } from 'react';

import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import { LoadingButton } from '@mui/lab';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';

import { withTenant } from '~/components/hoc';

import useToggleState from '~/hooks/useToggleState';

import { syncAllBusinesses } from '~/services/BusinessTraveller/BusinessTravellerService';

import ApiError from '~/types/ApiError';

interface Props {
  tenant: App.Tenant;
}

function AllBusinessesOrderSyncButton({ tenant }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const { isToggled: isModalOpen, toggleOn: openModal, toggleOff: closeModal } = useToggleState();

  const syncAllMutation = useMutation({
    mutationFn: async () => {
      return await syncAllBusinesses();
    },
    onSuccess: () => {
      enqueueSnackbar('Full order sync triggered. It will run in the background.', { variant: 'success' });
    },
    onError: (error: Error) => {
      if (error instanceof ApiError && error.status === 429) {
        enqueueSnackbar(error.message, { variant: 'warning' });
        return;
      }
      enqueueSnackbar(`Error triggering full order sync: ${error.message}`, { variant: 'error' });
    },
  });

  const handleSubmission = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      syncAllMutation.mutate();
      closeModal();
    },
    [syncAllMutation, closeModal],
  );

  if (tenant.brand !== 'lebusinesstraveller') {
    return null;
  }

  return (
    <>
      <Button variant="outlined" color="primary" onClick={openModal}>
        Synchronize All Orders
      </Button>
      <Dialog open={isModalOpen} onClose={closeModal} maxWidth="sm">
        <form onSubmit={handleSubmission}>
          <DialogTitle>Synchronize ALL Business Orders</DialogTitle>
          <DialogContent>
            <Typography gutterBottom>
              Synchronize orders for <strong>every business</strong> to the Business Traveller service.
            </Typography>
            <Typography gutterBottom>
              Note: This will only trigger the sync. The sync will run in the background and typically takes 15 minutes
              to complete. You will not be notified when it is complete.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button disabled={syncAllMutation.isPending} variant="text" onClick={closeModal}>
              Cancel
            </Button>
            <LoadingButton loading={syncAllMutation.isPending} type="submit" variant="contained" color="primary">
              Synchronize
            </LoadingButton>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
}

export default withTenant(AllBusinessesOrderSyncButton);
